import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AppBlockGuard } from '@core/services/guards/app/app-block-guard';
import { AuthGuard } from '@core/services/guards/auth-guard';
import { ClubMapfreGuard } from '@core/services/guards/club-mapfre-guard';
import { DevelopmentGuard } from '@core/services/guards/development-guard';
import { LoginGuard } from '@core/services/guards/login-guard';
import { LogoutGuard } from '@core/services/guards/logout-guard';
import { NoDirectAccessGuard } from '@core/services/guards/no-direct-access-guard';
import { OneTrustGuardService } from '@core/services/guards/one-trust-guard.service';
import { PwaAccessGuard } from '@core/services/guards/pwa-access-guard';

const routes: Routes = [
  {
    path: '',
    canActivateChild: [AppBlockGuard, PwaAccessGuard, OneTrustGuardService],
    children: [
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      {
        path: 'login',
        canActivate: [LoginGuard],
        loadChildren: () =>
          import('./modules/login/login.module').then(m => m.LoginModule)
      },
      {
        path: 'logout',
        canActivate: [LogoutGuard],
        children: []
      },
      {
        path: 'health-app',
        loadChildren: () => import('./modules/health-app/health-app.module').then( m => m.HealthAppPageModule)
      },
      {
        path: 'businesses',
        canActivate: [],
        loadChildren: () =>
          import('./modules/businesses/businesses.module').then(
            m => m.BudinessesModule
          )
      },
      {
        path: 'register',
        canActivate: [LoginGuard],
        loadChildren: () =>
          import('./modules/register/register.module').then(
             m => m.RegisterPageModule)
      },
      {
        path: 'result',
        canActivate: [NoDirectAccessGuard],
        loadChildren: () =>
          import('./modules/result/result.module').then(m => m.ResultPageModule)
      },
      {
        path: 'result-custom',
        canActivate: [NoDirectAccessGuard],
        loadChildren: () =>
          import('./modules/result-custom/result-custom.module').then(m => m.ResultCustomPageModule)
      },
      {
        path: 'vehicle-change',
        loadChildren: () =>
          import('./modules/vehicle-data/vehicle-change/vehicle-change.module').then(m => m.VehicleAddressChangeModule)
      },
      {
        path: 'support',
        loadChildren: () =>
          import('./modules/support/support.module').then(m => m.SupportModule)
      },
      {
        path: 'on-boarding',
        loadChildren: () =>
          import('./modules/on-boarding/on-boarding.module').then(m => m.OnBoardingModule)
      },
      {
        path: '',
        canActivateChild: [AuthGuard],
        children: [
          {
            path: 'vehicle-payment-change',
            loadChildren: () =>
              import(
                './modules/vehicle-data/vehicle-payment-change/vehicle-payment-change.module'
              ).then(m => m.VehiclePaymentChangeModule)
          },
          {
            path: 'bank-data',
            loadChildren: () =>
              import('./modules/bank-data/bank-data.module').then(
                m => m.BankDataModule
              )
          },
          {
            path: 'additional-coverages',
            loadChildren: () =>
              import('./modules/additional-coverages/additional-coverages.module').then(
                m => m.AdditionalCoveragesModule
              )
          },
          {
            path: 'inbox',
            loadChildren: () =>
              import('./modules/inbox/inbox.module').then(m => m.InboxModule)
          },
          {
            path: 'payment-plan',
            loadChildren: () =>
              import('./modules/payment-plan/payment-plan.module').then(
                m => m.PaymentPlanModule
              )
          },
          {
            path: 'profile',
            loadChildren: () =>
              import('./modules/profile/profile.module').then(
                m => m.ProfileModule
              )
          },
          {
            path: 'client-area',
            loadChildren: () =>
              import('./modules/client-area/client-area.module').then(
                m => m.ClientAreaModule
              )
          },
          {
            path: 'road-assistance',
            loadChildren: () =>
              import('./modules/road-assistance/road-assistance.module').then(
                m => m.RoadAssistanceModule
              )
          },
          // TODO NM-6283
          // {
          //   path: 'road-assist-tracing',
          //   loadChildren: () =>
          //     import('./modules/road-assist-tracing/road-assist-tracing.module').then(
          //       m => m.RoadAssistTracingModule
          //     )
          // },
          {
            path: 'families',
            loadChildren: () =>
              import('./modules/families/families.module').then(
                m => m.FamiliesModule
              )
          },
          {
            path: 'tecuidamos',
            canActivate: [ClubMapfreGuard],
            loadChildren: () =>
              import('./modules/tecuidamos/tecuidamos.module').then(
                m => m.TeCuidamosModule
              )
          },
          {
            path: 'superbonus',
            loadChildren: () =>
              import('./modules/superbonus/superbonus.module').then(
                m => m.SuperbonusModule
              )
          },
          {
            path: 'car-sinister',
            loadChildren: () =>
              import('./modules/car-sinister/car-sinister.module').then(
                m => m.CarSinisterModule
              )
          },
          {
            path: 'car-sinister-consultation',
            loadChildren: () =>
              import(
                './modules/car-sinister-consultation/car-sinister-consultation.module'
              ).then(m => m.CarSinisterConsultationModule)
          },
          {
            path: 'car-products-change',
            loadChildren: () =>
              import('./modules/car-change-product/car-change-product.module'
              ).then(m => m.CarChangeProductModule)
          },
          {
            path: 'contact',
            loadChildren: () =>
              import('./modules/contact/contact.module').then(m => m.ContactFormModule)
          },
          {
            path: 'help',
            loadChildren: () =>
              import('./modules/help/help.module').then(m => m.HelpModule)
          },
          {
            path: 'rating',
            loadChildren: () =>
              import('./modules/rating/rating.module').then(m => m.RatingModule)
          },
          {
            path: 'pension-plan-valuation',
            loadChildren: () =>
              import(
                './modules/pension-plan-valuation/pension-plan-valuation.module'
              ).then(m => m.PensionPlanValuationModule)
          },
          {
            canActivate: [NoDirectAccessGuard],
            path: 'pension-plan',
            loadChildren: () =>
              import(
                './modules/pension-plan/pension-plan.module'
              ).then(m => m.PensionPlanModule)
          },
          {
            path: 'update-doc',
            loadChildren: () =>
              import('./modules/update-doc/update-doc.module').then(
                m => m.UpdateDocModule
              )
          },
          {
            path: 'non-renewal',
            loadChildren: () =>
              import('./modules/non-renewal/non-renewal.module').then(
                m => m.NonRenewalModule
              )
          },
          {
            path: 'will-management',
            loadChildren: () =>
              import('./modules/will-management/will-management.module').then(
                m => m.WillManagmentModule
              )
          },
          {
            path: 'multimap',
            loadChildren: () =>
              import('./modules/multimap/multimap.module').then(
                m => m.MultiMapModule
              )
          },
          {
            path: 'medical-checkups',
            loadChildren: () =>
            import('./modules/medical-checkups/medical-checkups.module').then(
              m => m.MedicalCheckUpsModule
              )
          }
        ]
      },
      {
        path: '',
        children: [
          {
            path: 'misc',
            loadChildren: () =>
              import('./modules/miscellaneous/miscellaneous.module').then(
                m => m.MiscellaneousModule
              )
          },
          {
            path: 'operations',
            loadChildren: () =>
              import(
                './modules/public-operations/public-operations.module'
              ).then(m => m.PublicOperationsModule)
          },
          {
            path: 'receipt-payment',
            loadChildren: () =>
              import('./modules/receipt-payment/receipt-payment.module').then(
                m => m.ReceiptPaymentModule
              )
          },
          {
            path: 'patrimonial-sinister',
            loadChildren: () =>
              import(
                './modules/patrimonial-sinister/patrimonial-sinister.module'
              ).then(m => m.PatrimonialSinisterModule)
          },
          {
            path: 'locator',
            loadChildren: () =>
              import('./modules/locator/locator.module').then(
                m => m.LocatorModule
              )
          },
          {
            path: 'pwa-patrimonials',
            loadChildren: () =>
              import('./modules/pwa-patrimonials/pwa-patrimonials.module').then(
                m => m.PwaPatrimonialsModule
              )
          },
          {
            path: 'palette',
            canActivate: [DevelopmentGuard],
            loadChildren: () =>
              import('./palette/palette.module').then(m => m.PaletteModule)
          }
        ]
      },
      {
        path: 'deregister',
        canActivate: [],
        loadChildren: () =>
          import('./modules/deregister/deregister.module').then(m => m.DeregisterModule)
      },
      { path: '**', redirectTo: 'login' }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule]
})
export class AppRoutingModule {}
